import { installMultiplayerTestApi } from '../devtools/MultiplayerTestApi.js';
import { MultiplayerState } from '../engine/multiplayer/MultiplayerState.js';

export const MULTIPLAYER_INTEGRATION_VERSION = 1;

function assertMethod(value, method, name) {
  if (!value || typeof value[method] !== 'function') {
    throw new TypeError(`${name} must provide ${method}()`);
  }
}

export class MultiplayerIntegration {
  constructor({ app, windowRef = globalThis.window }) {
    if (!app || typeof app !== 'object') throw new TypeError('app must be an object');
    assertMethod(app.bus, 'on', 'app.bus');
    assertMethod(app.bus, 'emit', 'app.bus');
    assertMethod(app.simulationSnapshots, 'getStatus', 'app.simulationSnapshots');
    assertMethod(app.clock, 'snapshot', 'app.clock');
    this.app = app;
    this.windowRef = windowRef;
    this.state = new MultiplayerState();
    this.localPlayerId = null;
    this.installed = false;
    this.lastStep = null;
    this.lastError = null;
    this.detachInputStep = null;
    this.detachReset = null;
    this.detachSaveLoad = null;
    this.detachStateChange = null;
    this.inputProviders = new Set();
    this.onTestLabClick = (event) => this.#handleTestLabClick(event);
  }

  install() {
    if (this.installed) return this.getStatus();
    this.app.multiplayerState = this.state;
    this.app.multiplayer = this;
    this.app.runtimeState.multiplayerState = this.state.getStatus();
    this.app.runtimeState.multiplayerLocalPlayerId = null;

    this.app.testApi = installMultiplayerTestApi({
      baseApi: this.app.testApi,
      multiplayerState: this.state,
      storySystem: this.app.storySystem,
      bus: this.app.bus,
      clock: this.app.clock,
      simulationSnapshots: this.app.simulationSnapshots,
      getLocalPlayerId: () => this.localPlayerId,
      setLocalPlayerId: (playerId) => this.setLocalPlayerId(playerId),
      windowRef: this.windowRef,
    });
    if (this.app.testLab) this.app.testLab.testApi = this.app.testApi;

    this.detachInputStep = this.app.bus.on('input:step', (step) => this.#handleInputStep(step));
    this.detachReset = this.app.bus.on('simulation:reset', () => this.reset());
    this.detachSaveLoad = this.app.bus.on('save:loaded', () => this.reset());
    this.detachStateChange = this.app.bus.on('multiplayer-state:changed', () => this.#syncRuntime());
    this.#installTestLabControls();
    this.installed = true;
    this.#syncRuntime();
    return this.getStatus();
  }

  destroy() {
    if (!this.installed) return this.getStatus();
    this.detachInputStep?.();
    this.detachReset?.();
    this.detachSaveLoad?.();
    this.detachStateChange?.();
    this.detachInputStep = null;
    this.detachReset = null;
    this.detachSaveLoad = null;
    this.detachStateChange = null;
    this.inputProviders.clear();
    this.app.testLab?.root?.removeEventListener('click', this.onTestLabClick);
    this.reset();
    this.installed = false;
    this.#syncRuntime();
    return this.getStatus();
  }

  registerInputProvider(provider) {
    if (typeof provider !== 'function') throw new TypeError('input provider must be a function');
    this.inputProviders.add(provider);
    return () => this.inputProviders.delete(provider);
  }

  setLocalPlayerId(playerId) {
    if (playerId !== null && (typeof playerId !== 'string' || playerId.trim().length === 0)) {
      throw new TypeError('playerId must be null or a non-empty string');
    }
    this.localPlayerId = playerId === null ? null : playerId.trim();
    this.#syncRuntime();
    return this.localPlayerId;
  }

  reset() {
    this.localPlayerId = null;
    this.lastStep = null;
    this.lastError = null;
    const status = this.state.reset();
    this.#syncRuntime();
    return status;
  }

  getStatus() {
    return Object.freeze({
      version: MULTIPLAYER_INTEGRATION_VERSION,
      installed: this.installed,
      localPlayerId: this.localPlayerId,
      inputProviderCount: this.inputProviders.size,
      lastStep: this.lastStep,
      lastError: this.lastError,
      state: this.state.getStatus(),
    });
  }

  #handleInputStep(step) {
    if (!this.state.getStatus().active) return;
    try {
      if (this.state.getStatus().status === 'running') this.#submitProviderInputs(step);
      const player = this.localPlayerId === null ? null : this.state.getPlayer(this.localPlayerId);
      if (this.state.getStatus().status === 'running' && player?.status === 'connected') {
        const existing = this.state.listInputFrames({
          playerId: player.playerId,
          fromTick: step.tick,
          limit: 1,
        })[0];
        if (!existing || existing.tick !== step.tick) {
          this.state.submitInput({
            playerId: player.playerId,
            tick: step.tick,
            input: step.input,
          });
        }
      }
      const currentTick = this.state.getStatus().tick;
      if (currentTick === null || step.tick > currentTick) {
        this.lastStep = this.state.step({
          tick: step.tick,
          simulationTime: step.simulationTime,
          stepSeconds: step.stepSeconds,
        });
      }
      this.lastError = null;
      this.#syncRuntime();
      this.app.bus.emit('multiplayer:step', {
        simulationTick: step.tick,
        multiplayer: this.lastStep,
      });
    } catch (error) {
      this.lastError = error instanceof Error ? error.message : String(error);
      this.#syncRuntime();
      this.app.bus.emit('multiplayer:error', {
        tick: step.tick,
        error: this.lastError,
      });
    }
  }

  #submitProviderInputs(step) {
    for (const provider of this.inputProviders) {
      const frames = provider(step) ?? [];
      if (!Array.isArray(frames)) throw new TypeError('multiplayer input provider must return an array');
      for (const frame of frames) {
        if (!frame || typeof frame !== 'object' || Array.isArray(frame)) {
          throw new TypeError('multiplayer input provider frames must be objects');
        }
        if (typeof frame.playerId !== 'string' || frame.playerId.trim().length === 0) {
          throw new TypeError('multiplayer input provider frame playerId must be a non-empty string');
        }
        const playerId = frame.playerId.trim();
        const player = this.state.getPlayer(playerId);
        if (!player || player.status !== 'connected') continue;
        const existing = this.state.listInputFrames({
          playerId,
          fromTick: step.tick,
          limit: 1,
        })[0];
        if (existing?.tick === step.tick) continue;
        const result = this.state.submitInput({
          playerId,
          tick: step.tick,
          input: frame.input,
        });
        this.app.bus.emit('multiplayer:provider-input', {
          playerId,
          tick: step.tick,
          result,
        });
      }
    }
  }

  #syncRuntime() {
    if (!this.app.runtimeState) return;
    this.app.runtimeState.multiplayerState = this.state.getStatus();
    this.app.runtimeState.multiplayerLocalPlayerId = this.localPlayerId;
    const node = this.app.mount?.querySelector?.('[data-status="multiplayer"]');
    if (node) {
      const status = this.state.getStatus();
      node.textContent = status.active
        ? `${status.status} · ${status.connectedPlayerCount}/${status.playerCount}`
        : 'Inactive';
    }
  }

  #installTestLabControls() {
    const root = this.app.testLab?.root;
    const toolbar = root?.querySelector?.('.test-lab__toolbar');
    if (!root || !toolbar) return;
    if (!toolbar.querySelector('[data-action="multiplayer-lab"]')) {
      const launch = document.createElement('button');
      launch.type = 'button';
      launch.dataset.action = 'multiplayer-lab';
      launch.textContent = 'Multiplayer Lab';
      toolbar.append(launch);
    }
    if (!toolbar.querySelector('[data-action="multiplayer-status"]')) {
      const status = document.createElement('button');
      status.type = 'button';
      status.dataset.action = 'multiplayer-status';
      status.textContent = 'Multiplayer';
      toolbar.append(status);
    }
    root.addEventListener('click', this.onTestLabClick);

    const statusGrid = this.app.mount?.querySelector?.('.status-grid');
    if (statusGrid && !statusGrid.querySelector('[data-multiplayer-status-row]')) {
      const row = document.createElement('div');
      row.dataset.multiplayerStatusRow = '';
      row.innerHTML = '<dt>Multiplayer</dt><dd data-status="multiplayer">Inactive</dd>';
      statusGrid.append(row);
    }
  }

  #handleTestLabClick(event) {
    const action = event.target.closest('[data-action]')?.dataset.action;
    if (action !== 'multiplayer-lab' && action !== 'multiplayer-status') return;
    let result;
    try {
      if (action === 'multiplayer-status') {
        result = this.state.getDiagnostics();
      } else {
        this.reset();
        const tick = this.app.simulationSnapshots.getStatus().nextTick;
        this.state.createSession({
          sessionId: 'multiplayer-lab',
          hostPlayerId: 'host-snake',
          displayName: 'Host Snake',
          mode: 'cooperative',
          maxPlayers: 4,
          tick,
          metadata: { scenario: 'multiplayer-lab' },
        });
        this.state.joinPlayer({
          playerId: 'sibling-snake',
          displayName: 'Sibling Snake',
          tick,
        });
        this.state.setPlayerReady('host-snake', true, tick);
        this.state.setPlayerReady('sibling-snake', true, tick);
        this.state.startSession({ playerId: 'host-snake', tick });
        this.setLocalPlayerId('host-snake');
        const snake = this.app.snakeState?.snapshot?.() ?? null;
        if (snake) {
          this.state.claimAuthority({
            authorityId: `entity:${snake.entityId}`,
            playerId: 'host-snake',
            kind: 'player-snake',
          });
        }
        this.#syncRuntime();
        result = this.state.getDiagnostics();
      }
    } catch (error) {
      result = {
        ok: false,
        error: error instanceof Error ? error.message : String(error),
      };
    }
    const output = this.app.testLab?.root?.querySelector?.('.test-lab__output');
    if (output) output.textContent = JSON.stringify(result, null, 2);
  }
}
